import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Hạ tầng dùng chung cho các dropdown trong Payment Request, dựng theo pattern của
 * HRM Hub (`hrm-models-frontend/src/components/common/FilterDropdown.jsx`):
 *
 *   - popover render qua portal + position:fixed để không bị cắt bởi card
 *     overflow-hidden / cột form cuộn / drawer,
 *   - tính lại vị trí khi scroll & resize, kẹp trong viewport và lật lên trên khi
 *     dưới không đủ chỗ,
 *   - có ô tìm kiếm khi danh sách từ 5 lựa chọn trở lên, khớp cả label lẫn value,
 *   - dòng đang chọn tô nhạt màu accent + dấu check, có trạng thái "không khớp".
 *
 * Khác HRM ở kích thước: HRM dùng chữ 10–12px cho thanh filter, còn đây là form
 * nhập liệu nên dùng 14px và dòng cao 40px cho dễ đọc/dễ bấm.
 */

export const SEARCH_THRESHOLD = 5;

const isTextInput = (el) => el?.tagName === "INPUT" || el?.tagName === "TEXTAREA";

export const MENU_CLASS =
  "geist-scope tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden";

export const optionClass = (selected, active) => [
  // Compact 13px, full-strength text (user, 2026-10-22: 14–15px options were too large).
  "tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-h-[32px] tw-px-3 tw-py-1.5 tw-text-[13px] tw-leading-snug tw-text-left",
  "tw-cursor-pointer tw-select-none tw-transition-colors",
  selected ? "tw-bg-geist-accent/10 tw-font-semibold tw-text-geist-accent" : "tw-text-geist-fg",
  active && !selected ? "tw-bg-geist-100" : "",
  !active && !selected ? "hover:tw-bg-geist-100" : "",
].join(" ");

/** Bỏ dấu tiếng Việt để "Trang" khớp cả khi gõ "trang" lẫn "tráng". */
export const normalizeText = (v) =>
  String(v ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\u0111/g, "d");

export const matchOption = (option, query) => {
  const q = normalizeText(query).trim();
  if (!q) return true;
  return normalizeText(option?.label).includes(q) || normalizeText(option?.value).includes(q);
};

/**
 * Toạ độ popover bám theo trigger: mặc định mở xuống, lật lên khi thiếu chỗ, luôn
 * nằm trong viewport (chừa 8px mép).
 */
export const useAnchoredPosition = (open, anchorRef, { minWidth = 200, maxHeight = 320 } = {}) => {
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return undefined;

    const compute = () => {
      const r = anchorRef.current.getBoundingClientRect();
      const width = Math.max(minWidth, r.width);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));

      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const flip = below < Math.min(maxHeight, 220) && above > below;
      const space = Math.max(160, Math.min(maxHeight, flip ? above : below));

      setCoords(flip
        ? { bottom: window.innerHeight - r.top + 4, left, width, maxHeight: space }
        : { top: r.bottom + 4, left, width, maxHeight: space });
    };

    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open, anchorRef, minWidth, maxHeight]);

  return coords;
};

/** Đóng khi bấm ra ngoài trigger + popover. */
export const useOutsideClose = (open, refs, onClose) => {
  // refs là mảng literal và onClose là closure mới sau mỗi render -> nếu đưa vào
  // deps thì listener bị gỡ/gắn lại liên tục. Giữ qua ref, chỉ theo dõi `open`.
  const latest = useRef({ refs, onClose });
  latest.current = { refs, onClose };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const { refs: r, onClose: cb } = latest.current;
      if (!r.some((ref) => ref.current?.contains(e.target))) cb();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
};

/**
 * Điều hướng bàn phím cho listbox: ↑/↓ chạy dòng, Enter chọn, Escape đóng và trả
 * focus về trigger, Tab thoát. Trả về { activeIndex, setActiveIndex, onKeyDown }.
 */
export const useListboxKeys = ({ open, setOpen, count, onSelect, anchorRef }) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => { if (!open) setActiveIndex(-1); }, [open]);
  useEffect(() => { if (activeIndex >= count) setActiveIndex(count - 1); }, [count, activeIndex]);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) anchorRef.current?.focus?.();
  }, [setOpen, anchorRef]);

  const onKeyDown = useCallback((e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => (count === 0 ? -1 : (i + 1) % count));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => (count === 0 ? -1 : (i <= 0 ? count - 1 : i - 1)));
        break;
      // Trong ô tìm kiếm, Home/End thuộc về con trỏ text chứ không phải danh sách.
      case "Home":
        if (isTextInput(e.target)) break;
        e.preventDefault(); setActiveIndex(0); break;
      case "End":
        if (isTextInput(e.target)) break;
        e.preventDefault(); setActiveIndex(count - 1); break;
      case "Enter":
        if (activeIndex >= 0) { e.preventDefault(); onSelect(activeIndex); }
        break;
      case "Escape":
        e.preventDefault(); close(); break;
      case "Tab":
        close(false); break;
      default:
        break;
    }
  }, [open, setOpen, count, activeIndex, onSelect, close]);

  return { activeIndex, setActiveIndex, onKeyDown, close };
};

/** Cuộn dòng đang active vào tầm nhìn khi đi bằng bàn phím. */
export const useScrollActiveIntoView = (listRef, activeIndex) => {
  const prev = useRef(-1);
  useEffect(() => {
    if (activeIndex < 0 || activeIndex === prev.current) return;
    prev.current = activeIndex;
    const node = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, listRef]);
};
